import { useCallback, useEffect, useRef, useState } from 'react';
import type { ChangeEvent } from 'react';
import { useNavigate } from 'react-router-dom';
import { api } from '../lib/api.ts';
import { useBusyActions } from '../lib/useBusyActions.ts';
import { categoryPathToSlug } from '../lib/format.ts';
import ConfirmModal from '../components/ConfirmModal.tsx';
import MoveToCategoryModal from '../components/MoveToCategoryModal.tsx';
import DeckVisibilityModal from '../components/DeckVisibilityModal.tsx';
import BulkCategoryModal from '../components/BulkCategoryModal.tsx';
import DeckNameModal from '../components/DeckNameModal.tsx';
import SaveToLibraryModal from '../components/SaveToLibraryModal.tsx';
import ManageCategoriesModal from '../components/ManageCategoriesModal.tsx';
import CategorySidebar from '../components/CategorySidebar.tsx';
import ThemeToggle from '../components/ThemeToggle.tsx';
import AppHeader from '@shared-client/AppHeader.tsx';
import BuildsIndicator from '../components/BuildsIndicator.tsx';
import BuildsInProgress from '../components/BuildsInProgress.tsx';
import { useCoverWarm } from '../lib/coverWarm.ts';
import SettingsDrawer from '../canvas/SettingsDrawer.tsx';
import { Trash, Folder, Settings, Lock } from '../lib/icons.tsx';
import DeckInfoModal from '../components/DeckInfoModal.tsx';
import DeckOverview from '../canvas/DeckOverview.tsx';
import { cssVars } from '../lib/css.ts';
import { useMediaQuery, PHONE_QUERY } from '../lib/useMediaQuery.ts';
import { errorMessage } from '../lib/api.ts';
// The route's own parts, split out 2026-09-02 so the homepage redesign edits
// small files: the trash panel, the deck card, and the derived-data block.
import TrashList, { type TrashState } from './dashboard/TrashList.tsx';
import DeckCard, { NewDeckTile, TILE_MIN, type DeckCardActions } from './dashboard/DeckCard.tsx';
import DeckRow, { DeckRows } from './dashboard/DeckRows.tsx';
import DeckTable from './dashboard/DeckTable.tsx';
import { useDeckFilters, UNCATEGORISED } from './dashboard/useDeckFilters.ts';
import { useDeckFilterState } from './dashboard/useDeckFilterState.ts';
import DeckTitleRow from './dashboard/DeckTitleRow.tsx';
import DeckSearchBar from './dashboard/DeckSearchBar.tsx';
import ActiveFilterRow from './dashboard/ActiveFilterRow.tsx';
import FilterBar from './dashboard/FilterBar.tsx';
import RecentStrip from './dashboard/RecentStrip.tsx';
import DeckEmptyState from './dashboard/DeckEmptyState.tsx';
import { recentStripDecks } from './dashboard/recentStrip.ts';
import { filterBarModel } from './dashboard/filterOptions.ts';
import type { FilterChipAxis } from './dashboard/activeFilterChips.ts';
import type { ReactNode } from 'react';
import type { SaveToLibraryPayload } from '../components/SaveToLibraryModal.tsx';
import type { DeckSection, DeckSummary, SlideEntry } from '../../shared/deck.ts';
import type { Person } from '../../shared/organisation.ts';
import AccountMenu from '../components/AccountMenu.tsx';
import { currentUser } from '../lib/me.ts';
import type {
  CategoryColour,
  CategoryEntry,
  DeckDuplicatePayload,
  DeckImportPayload,
  DeckNameInfoPayload,
  DeckSummaryPayload,
  DecksPayload,
  LibrarySavePayload,
  RenameOk,
  TrashEntry,
  TrashPayload,
} from '../../shared/api.ts';

type DashboardState = {
  status: 'loading' | 'ready' | 'error';
  decks: DeckSummary[];
  /** The registry with its colours, straight from the payload. Superset of
   *  `categoryOrder`, which stays for readers that only want the paths. */
  categories: CategoryEntry[];
  categoryOrder: string[];
  /** The registry, so the visibility modal can name people and the owner
   *  column can resolve an id. Same list the payload already carried. */
  people: Person[];
  slugPrefix: string;
  error: string | null;
};

/**
 * A deck the user asked to LOOK at without opening the editor. The slides
 * arrive on their own fetch, so the three phases are a real sequence rather
 * than a formality — the card's button shows the loading one.
 */
type OverviewState = {
  slug: string;
  title: string;
} & (
  | { status: 'loading'; slides?: undefined; sections?: undefined; error?: undefined }
  | { status: 'ready'; slides: SlideEntry[]; sections: DeckSection[]; error?: undefined }
  | { status: 'error'; error: string; slides?: undefined; sections?: undefined }
);

/** One dialog, two verbs. `initialName` is the server's suggestion for a
 *  duplicate and the current title for a rename. */
type NameModalState = { mode: 'rename' | 'duplicate'; deck: DeckSummary; initialName: string };

/** What a pending confirmation carries; the modal's own cancel/busy are
 *  supplied at the render site. */
type PendingConfirm = {
  title: ReactNode;
  body?: ReactNode;
  danger?: boolean;
  confirmLabel?: string;
  onConfirm: () => void;
};

/**
 * Deck picker — a searchable grid of cards navigated by organisation category.
 * A deck sits in exactly ONE category (a sidebar tree, ≤2 levels), and carries
 * any number of flat `tags` beside it: the old second axis — a lifecycle
 * status where 'archived' and 'test' hid a deck from the grid — was removed
 * 2026-08-02 in favour of ordinary categories named "Archived" and "Test"
 * (migrated server-side by migrateDeckCategories), and the multi-category
 * spelling that briefly followed it was folded back to one home 2026-08-25. Trash is the one special
 * bucket left, because a trashed deck really is gone pending purge. A text
 * search and the publish chips narrow the grid; per-deck management (rename /
 * categories / duplicate / delete) lives in each card's kebab menu.
 */
export default function Dashboard() {
  const [state, setState] = useState<DashboardState>({
    status: 'loading',
    decks: [],
    categories: [],
    categoryOrder: [],
    people: [],
    slugPrefix: 'rkh-',
    error: null,
  });
  const [trash, setTrash] = useState<TrashState>({ entries: [], retentionDays: 7, loaded: false });
  // Whether the bin has ever had something in it THIS session — see the bounce
  // effect below for why an empty bin is two different situations.
  const trashHadEntries = useRef(false);
  // The whole view — query, status, category, model, updated window, sort, view
  // mode, tile size — as one object mirrored into the query string, so a
  // filtered dashboard is a link. Every control below reads and writes it.
  const {
    filters,
    setQuery,
    setStatus,
    setCategory: selectCategory,
    setModel,
    setOwner,
    setUpdated,
    setSort,
    setView,
    setTileSize,
    clearFilters,
  } = useDeckFilterState();
  const { query, status: activeStatus, category: activeCategory, tileSize } = filters;
  // One chip clears one axis. A lookup rather than a switch inside the row, so
  // the row stays a renderer and the setters stay where the rest of them are.
  const clearAxis = (axis: FilterChipAxis) => {
    if (axis === 'query') setQuery('');
    else if (axis === 'status') setStatus(null);
    else if (axis === 'category') selectCategory(null);
    else if (axis === 'model') setModel(null);
    else if (axis === 'owner') setOwner(null);
    else setUpdated(null);
  };
  const [showSettings, setShowSettings] = useState(false);
  const [openMenuKey, setOpenMenuKey] = useState<string | null>(null);
  const [confirm, setConfirm] = useState<PendingConfirm | null>(null);
  // A deck the user asked to look at without opening the editor. A medium or
  // large card shows its COVER, but that is one slide out of thirty — looking
  // properly means the deck's own "All slides" overview: every slide,
  // searchable, sizeable, and each tile still zooms to a live frame.
  // { slug, title, status, slides, sections, error }.
  const [overview, setOverview] = useState<OverviewState | null>(null);
  const [moveDeck, setMoveDeck] = useState<DeckSummary | null>(null);
  const [visibilityDeck, setVisibilityDeck] = useState<DeckSummary | null>(null);
  // Admins only. The server sends everyone's private decks solely when asked
  // (`?all=1`), so the default dashboard is the one a colleague sees rather
  // than a pile of other people's drafts. Not persisted: it is one click, and a
  // sticky "see everything" is the state you forget you are in.
  const [isAdmin, setIsAdmin] = useState(false);
  /** The signed-in person's id, for the Owner filter's `me` row. Null until
   *  /api/me answers, which makes "My decks" empty for that moment rather than
   *  showing everyone's. */
  const [meId, setMeId] = useState<string | null>(null);
  const [showAll, setShowAll] = useState(false);
  // Read-only, so it is never gated on `busy` the way the mutating items are:
  // "what was this built with" is exactly the question you want answered WHILE
  // something else is running.
  const [infoDeck, setInfoDeck] = useState<DeckSummary | null>(null);
  // Slug of the card being dragged onto the category rail | null.
  const [draggingSlug, setDraggingSlug] = useState<string | null>(null);
  // One dialog, two verbs.
  const [nameModal, setNameModal] = useState<NameModalState | null>(null);
  // Deck pending a whole-deck "Save to library" | null. The modal fetches the
  // slide list itself, so the card only has to name the deck.
  const [saveDeck, setSaveDeck] = useState<DeckSummary | null>(null);
  const [manageCats, setManageCats] = useState(false);
  // The sticky filter band: the scroll container it sticks inside, the band
  // itself (measured into `--band-h`), the sentinel above it that says whether
  // the page has scrolled, and the answer.
  const bodyRef = useRef<HTMLElement>(null);
  const bandRef = useRef<HTMLDivElement>(null);
  const sentinelRef = useRef<HTMLDivElement>(null);
  const [stuck, setStuck] = useState(false);
  const [bandHeight, setBandHeight] = useState(0);
  const isPhone = useMediaQuery(PHONE_QUERY);
  // Multi-select over the grid: slugs, in no particular order. Bulk verbs
  // (categories, delete) act on exactly this set; it survives filtering and
  // searching, and anything that vanishes from disk is pruned on the next load.
  const [selected, setSelected] = useState<Set<string>>(() => new Set());
  // Slug of the last checkbox clicked, for shift-click ranges over the grid.
  const anchorRef = useRef<string | null>(null);
  const [bulkCats, setBulkCats] = useState(false);
  // Trash multi-select: entry ids.
  const [trashSel, setTrashSel] = useState<Set<string>>(() => new Set());
  const { busy, run } = useBusyActions();
  const [importing, setImporting] = useState(false);
  const fileInputRef = useRef<HTMLInputElement>(null);
  const navigate = useNavigate();
  // slug → cover thumbnail URL. Held apart from `state.decks` so a cover the
  // warm queue rendered isn't thrown away by the next load() — the listing only
  // ever reports covers that are already CACHED, so merging can only ever add.
  const [covers, setCovers] = useState<Record<string, string | null>>({});
  const onCover = useCallback((slug: string, url: string) => setCovers((cur) => ({ ...cur, [slug]: url })), []);
  // Rendering a cover spawns the deck's Vite child, so this is off entirely at
  // the grid's small size, where there is no image to show. See lib/coverWarm.js
  // for why the dashboard is allowed to generate from a listing at all.
  //
  // The tile size is a GRID concept: the rows and list views show a thumb at
  // every size, so gating them on it would leave them on placeholders for
  // anyone whose saved size happened to be small (2026-09-02).
  const warm = useCoverWarm({ enabled: filters.view !== 'grid' || tileSize !== 'sm', onCover });

  // The overview needs the deck's slide list, which the dashboard listing
  // doesn't carry — same endpoint the editor uses. Fetched on demand (per
  // deck, only when asked) rather than for every card on the grid.
  function openOverview(deck: DeckSummary) {
    setOverview({ slug: deck.slug, title: deck.title, status: 'loading' });
    api
      .get<DeckSummaryPayload>(`/api/slides/${deck.slug}/_summary`)
      .then((sum) =>
        setOverview((cur) =>
          cur?.slug === deck.slug
            ? {
                slug: cur.slug,
                title: cur.title,
                status: 'ready',
                slides: sum.slides || [],
                sections: sum.sections || [],
              }
            : cur,
        ),
      )
      .catch((err) =>
        setOverview((cur) =>
          cur?.slug === deck.slug
            ? { slug: cur.slug, title: cur.title, status: 'error', error: errorMessage(err) }
            : cur,
        ),
      );
  }

  // Opening a slide from the overview lands in the editor ON that slide. The
  // editor restores its position from sessionStorage on first load, so seeding
  // that key IS the deep link (there is no ?slide= param).
  function openDeckAtSlide(slug: string, idx: number) {
    try {
      sessionStorage.setItem(`studio.lastSlide.${slug}`, String(idx));
    } catch {
      /* storage unavailable — the editor just opens at slide 1 */
    }
    navigate(`/editor/${slug}`);
  }

  useEffect(() => {
    currentUser().then((me) => {
      setIsAdmin(me?.isAdmin === true);
      setMeId(me?.person?.id ?? null);
    });
  }, []);

  function load() {
    // ?covers=1 — only this page draws a deck cover, and resolving one costs a
    // slide read per deck, so the publish drawer and the new-deck wizard read
    // the same route without it.
    return api
      .get<DecksPayload>('/api/decks?covers=1' + (showAll ? '&all=1' : ''))
      .then((data) => {
        const decks = data.decks || [];
        setCovers((cur) => {
          const next = { ...cur };
          for (const d of decks) if (d.cover) next[d.slug] = d.cover;
          return next;
        });
        // Drop anything from the selection that no longer exists (deleted,
        // renamed to a new slug): a bulk verb must never act on a ghost.
        setSelected((cur) => {
          if (!cur.size) return cur;
          const live = new Set(decks.map((d) => d.slug));
          const next = new Set([...cur].filter((s) => live.has(s)));
          return next.size === cur.size ? cur : next;
        });
        setState({
          status: 'ready',
          decks,
          categories: data.categories || [],
          categoryOrder: data.categoryOrder || [],
          people: data.people || [],
          slugPrefix: data.slugPrefix ?? 'rkh-',
          error: null,
        });
      })
      .catch((err) =>
        setState({
          status: 'error',
          decks: [],
          categories: [],
          categoryOrder: [],
          people: [],
          slugPrefix: 'rkh-',
          error: errorMessage(err),
        }),
      );
  }
  function loadTrash() {
    return api
      .get<TrashPayload>('/api/decks/trash')
      .then((data) => {
        const entries = data.trash || [];
        if (entries.length) trashHadEntries.current = true;
        setTrash({ entries, retentionDays: data.retentionDays || 7, loaded: true });
        setTrashSel((cur) => {
          if (!cur.size) return cur;
          const live = new Set(entries.map((e) => e.id));
          const next = new Set([...cur].filter((id) => live.has(id)));
          return next.size === cur.size ? cur : next;
        });
      })
      .catch(() => {}); // trash is non-critical; a failure shouldn't blank the dashboard
  }
  useEffect(() => {
    load();
    loadTrash();
    // `showAll` changes what the LIST route is asked for, so it refetches. Only
    // an admin can turn it on, and the server ignores it for everybody else.
    // eslint-disable-next-line react-hooks/exhaustive-deps -- load/loadTrash are re-created every render
  }, [showAll]);

  // Leaving an EMPTIED Trash view: once the last entry is restored or purged,
  // the rail's row vanishes, so fall back to the default deck view rather than
  // leaving the user on a bin with no way back except the browser.
  //
  // Two guards, both of which were missing and both of which mattered. `loaded`
  // is because the entries start as `[]` before the fetch answers, so a
  // `?status=trash` link bounced itself off the bin before the bin had been
  // read — the deep link simply never worked. `trashHadEntries` is because an
  // ALREADY-empty bin is a different situation from one you just emptied: there
  // is nothing to bounce away FROM, and the empty state below says so and
  // offers the way back in words.
  useEffect(() => {
    if (activeStatus === 'trash' && trash.loaded && trash.entries.length === 0 && trashHadEntries.current) {
      setStatus(null);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [activeStatus, trash.loaded, trash.entries.length]);

  // Click-away and Escape for the card menus live in DeckMenu itself
  // (`useDismissMenu`, which tests containment against the menu AND its kebab).
  // The page-level blunt dismisser that used to sit here closed on ANY
  // pointerdown, unmounting the menu before a click could reach its item.

  // ---- the sticky filter band ---------------------------------------------
  // The pills and their chips stay under the app header while the grid scrolls
  // (2026-09-07). Two measurements make that work and neither is a scroll
  // handler: an IntersectionObserver on a 1px sentinel says whether the page
  // has moved, and a ResizeObserver on the band publishes its height as
  // `--band-h` so everything else that sticks — the rail, the bulk bar — knows
  // where the band ends. Reading either on every scroll event would be layout
  // thrash on a page whose body is a long grid.
  //
  // Nothing sticks on a phone but the app header itself (mobile-layout.md), so
  // the height is published as zero there rather than the rules being
  // duplicated in a media query the JSX cannot see.
  useEffect(() => {
    const sentinel = sentinelRef.current;
    const root = bodyRef.current;
    if (!sentinel || !root || isPhone) {
      setStuck(false);
      return;
    }
    const io = new IntersectionObserver((entries) => setStuck(!entries[entries.length - 1]?.isIntersecting), {
      root,
      threshold: 0,
    });
    io.observe(sentinel);
    return () => io.disconnect();
  }, [isPhone, state.status, state.decks.length]);

  useEffect(() => {
    const band = bandRef.current;
    if (!band) {
      setBandHeight(0);
      return;
    }
    const measure = () => setBandHeight(band.offsetHeight);
    measure();
    // The band's height changes with the chip row, which wraps — so it is a
    // resize question, not a mount-time one.
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(measure);
    ro.observe(band);
    return () => ro.disconnect();
  }, [state.status, state.decks.length]);

  // The internal /?cat= deep-link for a category (applies the filter). The
  // param was `category` before the view moved into the URL; useDeckFilterState
  // still READS that name, so links handed out before this change keep working.
  function onCopyFilterLink(path: string) {
    const key = path === UNCATEGORISED ? 'uncategorised' : path;
    const url = `${window.location.origin}/?cat=${encodeURIComponent(key)}`;
    navigator.clipboard.writeText(url).catch(() => window.alert('Copy failed'));
  }

  // The public, login-free /c/<slug> viewer for a category (published decks).
  function onCopyPublicLink(path: string) {
    const url = `${window.location.origin}/c/${categoryPathToSlug(path)}`;
    navigator.clipboard.writeText(url).catch(() => window.alert('Copy failed'));
  }

  // ---- category management (Manage Categories modal) ----------------------
  async function onCreateCategory(path: string) {
    await run(async () => {
      await api.post('/api/categories/create', { path });
      await load();
    }, 'Creating category failed');
  }

  async function onRenameCategoryPath(from: string, to: string) {
    await run(async () => {
      await api.put('/api/categories/rename', { from, to });
      await load();
    }, 'Renaming category failed');
  }

  async function onReorderCategories(order: string[]) {
    await run(async () => {
      await api.put('/api/categories/order', { order });
      await load();
    }, 'Reordering categories failed');
  }

  // Tint a category, or clear it with null. The rail and the card chips read
  // the colour off the tree, so a reload is what makes the change visible.
  async function onSetCategoryColour(path: string, colour: CategoryColour | null) {
    await run(async () => {
      await api.put('/api/categories/colour', { path, colour });
      await load();
    }, 'Setting category colour failed');
  }

  // Deletes only untag decks (they fall back to Uncategorised) — never the decks.
  function onDeleteCategoryPath(path: string) {
    const p = path.toLowerCase();
    const count = state.decks.filter((d) => {
      const c = (d.category || '').toLowerCase();
      return c === p || c.startsWith(p + '/');
    }).length;
    setConfirm({
      title: `Delete the "${path}" category?`,
      body: `It will be removed from ${count} deck${count === 1 ? '' : 's'} (and any sub-categories). The decks aren't deleted: they move to Uncategorised.`,
      danger: true,
      confirmLabel: 'Delete category',
      onConfirm: async () => {
        await run(async () => {
          await api.post('/api/categories/delete', { path });
          await load();
        }, 'Deleting category failed');
        setConfirm(null);
      },
    });
  }

  async function logout() {
    await api.post('/api/logout');
    window.location.href = '/login';
  }

  // Rename: the user gives a NAME, the server derives the slug. When the slug
  // changes the deck's directory moves and a published deck goes offline, so say
  // what happened rather than silently reloading (the dialog warned first).
  async function onRenameConfirm(name: string) {
    const deck = nameModal?.deck;
    if (!deck) return;
    const res = await run(() => api.put<RenameOk>(`/api/decks/${deck.slug}/rename`, { name }), 'Rename failed');
    // `run` answers `undefined` on failure and `true` for a VOID success; this
    // call returns a body, so both arms mean "nothing to report".
    if (!res || res === true) return;
    setNameModal(null);
    await load();
    if (res.unpublished) {
      window.alert(
        `Renamed to "${res.title}". Its share link is now /p/${res.slug}/ and the deck has been unpublished: publish it again to share the new link.`,
      );
    }
  }

  // Move a deck to ONE category, or to Uncategorised with null. The route is a
  // PUT of the deck's whole home, so there is nothing to merge.
  async function onSetDeckCategory(deck: DeckSummary, category: string | null) {
    await run(async () => {
      await api.put(`/api/decks/${deck.slug}/category`, { category });
      setMoveDeck(null);
      await load();
    }, 'Saving category failed');
  }

  // Who can open the deck. `null` opens it to everyone, `[]` is owner-only, a
  // list is owner-plus-those-people — the route stores exactly what it is sent.
  async function onSetDeckVisibility(deck: DeckSummary, restrictedTo: string[] | null) {
    await run(async () => {
      await api.put(`/api/decks/${deck.slug}/visibility`, { restrictedTo });
      setVisibilityDeck(null);
      await load();
    }, 'Saving the privacy setting failed');
  }

  // Dropping a card on a sidebar row MOVES it there: a deck has one home, so
  // the drop replaces it. The Uncategorised row clears it, which is the same
  // thing with nowhere to go.
  async function onDropDeckOnCategory(path: string | null, slug: string) {
    const deck = state.decks.find((d) => d.slug === slug);
    if (!deck) return;
    // A null path is the Uncategorised row with no sentinel supplied; it means
    // the same thing the sentinel does.
    const next = path === null || path === UNCATEGORISED ? null : path;
    const current = deck.category ?? null;
    if ((next ?? '').toLowerCase() === (current ?? '').toLowerCase()) return; // already where it was dropped
    await run(async () => {
      await api.put(`/api/decks/${slug}/category`, { category: next });
      await load();
    }, 'Filing the deck failed');
  }

  // Download the deck's source as a .zip. A same-origin anchor click lets the
  // browser stream the attachment with the session cookie attached (no fetch
  // round-trip into memory).
  function onExport(deck: DeckSummary) {
    const a = document.createElement('a');
    a.href = `/api/decks/${deck.slug}/export`;
    a.rel = 'noopener';
    document.body.appendChild(a);
    a.click();
    a.remove();
  }

  // Upload a deck .zip (produced by Export here or `bun run` locally) into this
  // Studio. Multipart, field name "file" — matches POST /api/decks/import.
  async function onImportFile(e: ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    e.target.value = ''; // allow re-selecting the same file
    if (!file) return;
    setImporting(true);
    try {
      const form = new FormData();
      form.append('file', file);
      const data = await api.postForm<DeckImportPayload>('/api/decks/import', form);
      await load();
      if (data.slug) navigate(`/editor/${data.slug}`);
    } catch (err) {
      window.alert('Import failed: ' + errorMessage(err));
    } finally {
      setImporting(false);
    }
  }

  // Open the rename/duplicate dialog. Duplicate asks the server for its default
  // name (the next free "<name> N") so the suggestion is already known-unique.
  async function openNameModal(mode: NameModalState['mode'], deck: DeckSummary) {
    let initialName = deck.title;
    if (mode === 'duplicate') {
      try {
        const info = await api.get<DeckNameInfoPayload>(`/api/decks/${deck.slug}/name-info`);
        initialName = info.duplicateName;
      } catch {
        initialName = ''; // let the user name it; the dialog validates either way
      }
    }
    setNameModal({ mode, deck, initialName });
  }

  async function onDuplicateConfirm(name: string) {
    const deck = nameModal?.deck;
    if (!deck) return;
    const res = await run(
      () => api.post<DeckDuplicatePayload>(`/api/decks/${deck.slug}/duplicate`, { name }),
      'Duplicate failed',
    );
    if (!res || res === true) return;
    setNameModal(null);
    await load();
    navigate(`/editor/${res.newSlug}`);
  }

  // Save a whole deck into the RKH library as ONE item, keeping its sections
  // (routes/library.ts save). Same modal the deck editor uses; the editor's
  // Overview is the place to save a subset.
  async function onSaveToLibrary(target: SaveToLibraryPayload) {
    const deck = saveDeck;
    setSaveDeck(null);
    if (!deck) return;
    const res = await run(
      () => api.post<LibrarySavePayload>('/api/library/save', { fromSlug: deck.slug, bank: 'library', ...target }),
      'Save to library failed',
    );
    if (res !== true && res?.count) {
      window.alert(
        `Saved ${res.count} slide${res.count === 1 ? '' : 's'} from "${deck.title || deck.slug}" to the RKH Library.`,
      );
    }
  }

  function onCopyShareLink(deck: DeckSummary) {
    if (!deck.publishedUrl) return;
    navigator.clipboard.writeText(deck.publishedUrl).catch(() => window.alert('Copy failed'));
  }

  function onDelete(deck: DeckSummary) {
    const days = trash.retentionDays;
    setConfirm({
      title: 'Delete this deck?',
      body: `${deck.title} moves to Trash, where it's kept for ${days} day${days === 1 ? '' : 's'} before it's permanently removed. You can restore it any time before then.`,
      danger: true,
      confirmLabel: 'Move to Trash',
      onConfirm: async () => {
        await run(async () => {
          await api.delete(`/api/decks/${deck.slug}`);
          await Promise.all([load(), loadTrash()]);
        }, 'Delete failed');
        setConfirm(null);
      },
    });
  }

  async function onRestore(entry: TrashEntry) {
    await run(async () => {
      await api.post(`/api/decks/trash/${entry.id}/restore`);
      await Promise.all([load(), loadTrash()]);
    }, 'Restore failed');
  }

  function onPurge(entry: TrashEntry) {
    setConfirm({
      title: 'Delete permanently?',
      body: `${entry.title} will be removed for good right now. This can't be undone.`,
      danger: true,
      confirmLabel: 'Delete permanently',
      onConfirm: async () => {
        await run(async () => {
          await api.delete(`/api/decks/trash/${entry.id}`);
          await loadTrash();
        }, 'Delete failed');
        setConfirm(null);
      },
    });
  }

  // ---- multi-select over the grid ----------------------------------------
  // A checkbox toggles one deck; shift-click extends from the last one clicked
  // across the CURRENT view order, which is the only order the user can see.
  function toggleSelected(slug: string, shiftKey: boolean) {
    setSelected((cur) => {
      const next = new Set(cur);
      const order = flatDecks.map((d) => d.slug);
      const anchor = anchorRef.current;
      if (shiftKey && anchor && anchor !== slug && order.includes(anchor) && order.includes(slug)) {
        const [a, b] = [order.indexOf(anchor), order.indexOf(slug)].sort((x, y) => x - y) as [number, number];
        // A shift-range always ADDS — the same rule as a file manager, and it
        // means a mis-aimed range never silently clears what you had.
        for (const s of order.slice(a, b + 1)) next.add(s);
      } else if (next.has(slug)) next.delete(slug);
      else next.add(slug);
      return next;
    });
    anchorRef.current = slug;
  }

  function clearSelection() {
    setSelected(new Set());
    anchorRef.current = null;
  }

  // Selected decks, in view order, restricted to what is actually on screen —
  // `selected` survives filtering, so this is what the verbs act on.
  const selectedDecks = () => flatDecks.filter((d) => selected.has(d.slug));

  // Move every selected deck to ONE category (null = Uncategorised) in a single
  // request. The bulk route's `categories` op is a move, not a delta: it reads
  // the destination from `add[0]`, and an empty `add` unfiles the decks.
  async function onBulkCategories({ category }: { category: string | null }) {
    const decks = selectedDecks().filter((d) => (d.category ?? null) !== category);
    setBulkCats(false);
    if (!decks.length) return;
    await run(async () => {
      const res = await api.post<{ ok: true; failed: { slug: string; error: string }[] }>('/api/decks/bulk', {
        op: 'categories',
        slugs: decks.map((d) => d.slug),
        add: category ? [category] : [],
      });
      await load();
      if (res.failed.length) {
        throw new Error(`${res.failed.length} of ${decks.length} could not be moved: ${res.failed[0]?.error}`);
      }
    }, 'Saving category failed');
  }

  function onBulkDelete() {
    const decks = selectedDecks();
    if (!decks.length) return;
    const days = trash.retentionDays;
    setConfirm({
      title: `Delete ${decks.length} deck${decks.length === 1 ? '' : 's'}?`,
      body: `They move to Trash, where they're kept for ${days} day${days === 1 ? '' : 's'} before being permanently removed. You can restore them any time before then.`,
      danger: true,
      confirmLabel: `Move ${decks.length} to Trash`,
      onConfirm: async () => {
        await run(async () => {
          // Sequential: each delete takes the deck lock server-side, and a
          // failure part-way through should stop rather than pile up alerts.
          for (const deck of decks) await api.delete(`/api/decks/${deck.slug}`);
          await Promise.all([load(), loadTrash()]);
        }, 'Delete failed');
        clearSelection();
        setConfirm(null);
      },
    });
  }

  // ---- trash multi-select --------------------------------------------------
  function toggleTrash(id: string) {
    setTrashSel((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  }
  function toggleAllTrash(on: boolean) {
    setTrashSel(on ? new Set(trash.entries.map((e) => e.id)) : new Set());
  }

  async function onRestoreSelected() {
    const entries = trash.entries.filter((e) => trashSel.has(e.id));
    if (!entries.length) return;
    await run(async () => {
      for (const entry of entries) await api.post(`/api/decks/trash/${entry.id}/restore`);
      await Promise.all([load(), loadTrash()]);
    }, 'Restore failed');
    setTrashSel(new Set());
  }

  function onPurgeSelected() {
    const entries = trash.entries.filter((e) => trashSel.has(e.id));
    if (!entries.length) return;
    setConfirm({
      title: `Delete ${entries.length} deck${entries.length === 1 ? '' : 's'} permanently?`,
      body: `They will be removed for good right now. This can't be undone.`,
      danger: true,
      confirmLabel: 'Delete permanently',
      onConfirm: async () => {
        await run(async () => {
          for (const entry of entries) await api.delete(`/api/decks/trash/${entry.id}`);
          await loadTrash();
        }, 'Delete failed');
        setTrashSel(new Set());
        setConfirm(null);
      },
    });
  }

  async function onChangeRetention(days: string) {
    const n = Math.min(365, Math.max(1, Math.round(Number(days) || 0)));
    if (!n || n === trash.retentionDays) return;
    await run(async () => {
      await api.put('/api/settings', { trashRetentionDays: n });
      setTrash((t) => ({ ...t, retentionDays: n }));
    }, 'Saving retention failed');
  }

  // ---- derived data -------------------------------------------------------
  const {
    visibleDecks,
    flatDecks,
    categoryTree,
    allCategories,
    uncategorisedCount,
    publishedCount,
    draftCount,
    models,
    showTrash,
    selectedCount,
  } = useDeckFilters({
    decks: state.decks,
    categories: state.categories,
    filters,
    selected,
    meId,
  });

  // A folder chip wears its registry tint, the same one the rail row will. Read
  // straight off the registry rather than walked out of the category tree: the
  // tree only carries paths some deck actually uses, and a filter can name an
  // empty category.
  const categoryColour = (path: string): CategoryColour | null =>
    state.categories.find((c) => c.path.toLowerCase() === path.toLowerCase())?.colour ?? null;

  // An owner id back to a name, for the chip row. Null for somebody who has
  // been deleted from the registry since the deck was attributed — the chip
  // then reads the raw id, which is still something you can see and clear.
  const personName = (id: string): string | null => state.people.find((p) => p.id === id)?.name ?? null;

  /* The rail carried a VIEWS section — All decks, Recently opened, Published,
     Drafts, Trash — from Run 4 until 2026-09-07, and it is gone because every
     row of it was a second control for a question the page already asked
     somewhere better: Published, Drafts and Trash are the Status pill, All
     decks is that pill's own "All" plus the rail's folder row, and Recently
     opened was the Sort pill's "Last opened" and the Jump back in strip
     wearing a filter's clothes. The shared CategorySidebar keeps the `views`
     prop for the Library, whose three rows are the only control their axes
     have. */

  // Everything a card's kebab menu (and its zoom button) can do. Held as one
  // object so the card takes a single prop rather than nine.
  const cardActions: DeckCardActions = {
    onOpenOverview: openOverview,
    onRename: (d) => openNameModal('rename', d),
    onInfo: setInfoDeck,
    onCategories: setMoveDeck,
    onVisibility: setVisibilityDeck,
    onDuplicate: (d) => openNameModal('duplicate', d),
    onSaveToLibrary: setSaveDeck,
    onExport,
    onCopyShareLink,
    onDelete,
  };

  return (
    <div className="dashboard" style={cssVars({ '--band-h': isPhone ? '0px' : `${bandHeight}px` })}>
      <AppHeader
        active="decks"
        onNavigate={navigate}
        bar={
          /* No search here any more (2026-09-02): it is a full-width bar under
             the page title, where it can carry a real placeholder and a ⌘K
             hint. Library and Bank still pass their own — the slot is
             per-surface, which is exactly why moving this one needed no change
             to AppHeader.
             The file input stays mounted at every width: the Import action
             below drives it through the ref, and it is the same input whether
             the button was pressed in the bar or in the sheet. */
          <input ref={fileInputRef} type="file" accept=".zip,application/zip" hidden onChange={onImportFile} />
        }
        actions={[
          {
            key: 'import',
            label: importing ? 'Importing…' : 'Import',
            barClassName: 'btn btn--ghost',
            hint: 'Import a deck’s Export (editable .zip), not a Portable or Web-build download',
            disabled: importing,
            onSelect: () => fileInputRef.current?.click(),
          },
          // Safe to leave the bar on narrow screens: the deck grid already
          // ends with a ghost "+ New deck" tile doing the same job.
          { key: 'new', label: 'New deck', primary: true, onSelect: () => navigate('/new') },
          { key: 'builds', label: 'Builds', render: () => <BuildsIndicator /> },
          {
            key: 'theme',
            label: 'Appearance',
            ownRow: true,
            render: ({ inSheet }) => <ThemeToggle inSheet={inSheet} />,
          },
          {
            key: 'settings',
            label: 'Settings',
            // An ICON in the bar since 2026-08-07, so it takes the square icon
            // wrapper rather than `.btn`'s text pill, which rendered it 39x31.
            barLabel: <Settings />,
            barClassName: 'iconbtn',
            hint: 'Models, engines, storage',
            onSelect: () => setShowSettings(true),
          },
          {
            key: 'account',
            label: 'Sign out',
            ownRow: true,
            render: ({ inSheet }) => <AccountMenu inSheet={inSheet} onSignOut={logout} />,
          },
        ]}
      />
      {showSettings && <SettingsDrawer onClose={() => setShowSettings(false)} />}

      <main className="dashboard__body" ref={bodyRef}>
        {/* Builds in flight, where you'd come looking for the deck. A deck
            being built has no card yet (it isn't on disk until the scaffold
            lands, and it isn't finished until it is), so without this row the
            only trace of a build you walked away from is the top-bar
            indicator. */}
        <BuildsInProgress />
        {state.status === 'loading' && <p className="muted">Loading decks…</p>}
        {state.status === 'error' && (
          <div className="dashboard__error">
            <strong>Couldn't load decks</strong>
            <p className="muted">{state.error}</p>
            <button type="button" className="btn btn--ghost btn--sm" onClick={load}>
              Retry
            </button>
          </div>
        )}
        {state.status === 'ready' && state.decks.length === 0 && (
          <div className="dashboard__empty">
            <h2>No decks yet</h2>
            <p className="muted">Describe your first deck and the studio builds it with you.</p>
            <button type="button" className="btn btn--primary" onClick={() => navigate('/new')}>
              Create a deck
            </button>
          </div>
        )}

        {/* The top of the page: what this is, what the studio holds, and how to
            search it. Only once there are decks — a title over an empty state
            would be a heading with nothing under it.
            The title and the search SCROLL AWAY; the band below them does not,
            which is why they are two blocks rather than one. */}
        {state.status === 'ready' && state.decks.length > 0 && (
          <div className="dashboard__top">
            <DeckTitleRow
              counts={{ total: visibleDecks.length, published: publishedCount, drafts: draftCount }}
              actions={
                isAdmin ? (
                  <button
                    type="button"
                    className={'tag-chip tag-chip--filter' + (showAll ? ' is-active' : '')}
                    aria-pressed={showAll}
                    onClick={() => setShowAll((v) => !v)}
                    title="Admins can open any deck. This shows the private ones in the list too."
                  >
                    <Lock size={11} />
                    Show private decks
                  </button>
                ) : undefined
              }
            />
            <DeckSearchBar
              query={query}
              onQuery={setQuery}
              resultCount={flatDecks.length}
              totalCount={visibleDecks.length}
            />
          </div>
        )}

        {/* The sentinel is the whole of the "have we scrolled" question: an
            IntersectionObserver on a 1px marker just above the band, rather
            than a scroll listener reading offsets every frame. */}
        {state.status === 'ready' && state.decks.length > 0 && (
          <div ref={sentinelRef} className="dashboard__band-sentinel" aria-hidden="true" />
        )}

        {/* The filter band: the pills and the chips that explain them, stuck
            together under the app header while the grid scrolls beneath. They
            are ONE block because they are one thought — what is narrowing this
            page, and how to stop it — and a chip row that scrolled away from
            the pills it describes would leave the page filtered with nothing on
            screen saying so. */}
        {state.status === 'ready' && state.decks.length > 0 && (
          <div ref={bandRef} className={'dashboard__band' + (stuck ? ' is-stuck' : '')}>
            <FilterBar
              model={filterBarModel({
                decks: visibleDecks,
                filters,
                categoryTree,
                models,
                people: state.people,
                meId,
                trashCount: trash.entries.length,
              })}
              onStatus={setStatus}
              onFolder={selectCategory}
              onModel={setModel}
              onOwner={setOwner}
              onUpdated={setUpdated}
              onSort={setSort}
              view={filters.view}
              onView={setView}
              tileSize={tileSize}
              onTileSize={setTileSize}
            />
            <ActiveFilterRow
              filters={filters}
              models={models}
              categoryColour={categoryColour}
              personName={personName}
              shown={flatDecks.length}
              total={visibleDecks.length}
              trashCount={trash.entries.length}
              onClearAxis={clearAxis}
              onClearAll={clearFilters}
            />
          </div>
        )}

        {/* The rail, and beside it whichever view is selected. The trash is one
            of those views, so the guard has to survive a studio whose every deck
            is in the bin: `decks.length` is 0 there, and gating on it alone put
            the bin behind a rail that had unmounted itself. */}
        {state.status === 'ready' && (state.decks.length > 0 || showTrash) && (
          <div className="dashboard__layout">
            <CategorySidebar
              tree={categoryTree}
              active={activeCategory}
              allCount={visibleDecks.length}
              uncategorisedCount={uncategorisedCount}
              uncategorisedKey={UNCATEGORISED}
              onSelect={selectCategory}
              onManage={() => setManageCats(true)}
              onDropDeck={onDropDeckOnCategory}
              dragging={!!draggingSlug}
            />
            <div className="dashboard__main">
              {/* Trash is a VIEW now that the rail has a row for it, so it renders
                  here beside the rail rather than in place of the whole layout:
                  unmounting the rail to show the bin left no way back to the decks
                  except the browser's back button. */}
              {showTrash && trash.entries.length === 0 ? (
                /* An empty bin, arrived at by link or by restoring the last
                   deck in it. The panel itself would be a retention field over
                   a select-all with nothing to select. */
                <DeckEmptyState
                  filters={filters}
                  models={models}
                  trashRetentionDays={trash.retentionDays}
                  personName={personName}
                  onClearFilters={clearFilters}
                  onCreateDeck={() => navigate('/new')}
                />
              ) : showTrash ? (
                <TrashList
                  trash={trash}
                  busy={busy}
                  selected={trashSel}
                  onToggle={toggleTrash}
                  onToggleAll={toggleAllTrash}
                  onRestoreSelected={onRestoreSelected}
                  onPurgeSelected={onPurgeSelected}
                  onRestore={onRestore}
                  onPurge={onPurge}
                  onChangeRetention={onChangeRetention}
                />
              ) : (
                <>
                  {/* "Jump back in": the last few decks opened, above whatever
                    body is showing. It hides itself the moment anything is
                    narrowing the page — the module owns that rule, so this is
                    an unconditional render of a possibly-empty list. */}
                  <RecentStrip decks={recentStripDecks({ decks: visibleDecks, filters })} covers={covers} warm={warm} />
                  {/* Bulk bar: only once something is ticked, so the default view is
                    unchanged. "Select all" means all decks in the CURRENT view —
                    the filter is the selection you already made. */}
                  {selectedCount > 0 && (
                    <div className="bulk-bar" role="region" aria-label="Selected decks">
                      <strong className="bulk-bar__count">{selectedCount} selected</strong>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setSelected(new Set(flatDecks.map((d) => d.slug)))}
                        disabled={busy || selectedCount === flatDecks.length}
                      >
                        Select all {flatDecks.length}
                      </button>
                      <button type="button" className="btn btn--ghost btn--sm" onClick={clearSelection} disabled={busy}>
                        Clear
                      </button>
                      <span className="bulk-bar__spacer" />
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm"
                        onClick={() => setBulkCats(true)}
                        disabled={busy}
                      >
                        <Folder /> Categories…
                      </button>
                      <button
                        type="button"
                        className="btn btn--ghost btn--sm bulk-bar__delete"
                        onClick={onBulkDelete}
                        disabled={busy}
                      >
                        <Trash /> Delete
                      </button>
                    </div>
                  )}
                  {/* Every way of ending up with nothing on screen, each saying
                    which filter did it and offering the same clear-all the chip
                    row's button calls. `filtering` is not consulted here: the
                    module decides, so an unnarrowed-but-empty view (every deck
                    is a bank) gets a sentence too rather than a blank column. */}
                  {flatDecks.length === 0 ? (
                    <DeckEmptyState
                      filters={filters}
                      models={models}
                      personName={personName}
                      onClearFilters={clearFilters}
                      onCreateDeck={() => navigate('/new')}
                    />
                  ) : (
                    <>
                      {filters.view === 'grid' && (
                        <ul className="deck-grid" role="list" style={cssVars({ '--tile-min': TILE_MIN[tileSize] })}>
                          {flatDecks.map((d) => (
                            <DeckCard
                              key={d.slug}
                              deck={d}
                              cardKey={d.slug}
                              tileSize={tileSize}
                              cover={covers[d.slug] || null}
                              warm={warm}
                              overviewLoading={overview?.slug === d.slug && overview.status === 'loading'}
                              busy={busy}
                              isSelected={selected.has(d.slug)}
                              isSelecting={selected.size > 0}
                              isDragging={draggingSlug === d.slug}
                              menuOpen={openMenuKey === d.slug}
                              onToggleSelected={toggleSelected}
                              setMenuKey={setOpenMenuKey}
                              setDraggingSlug={setDraggingSlug}
                              actions={cardActions}
                              categoryColour={categoryColour}
                            />
                          ))}
                          <NewDeckTile onClick={() => navigate('/new')} />
                        </ul>
                      )}
                      {filters.view === 'rows' && (
                        <DeckRows>
                          {flatDecks.map((d) => (
                            <DeckRow
                              key={d.slug}
                              deck={d}
                              cardKey={d.slug}
                              cover={covers[d.slug] || null}
                              warm={warm}
                              busy={busy}
                              isSelected={selected.has(d.slug)}
                              isSelecting={selected.size > 0}
                              isDragging={draggingSlug === d.slug}
                              menuOpen={openMenuKey === d.slug}
                              categoryColour={categoryColour}
                              onToggleSelected={toggleSelected}
                              setMenuKey={setOpenMenuKey}
                              setDraggingSlug={setDraggingSlug}
                              actions={cardActions}
                            />
                          ))}
                        </DeckRows>
                      )}
                      {filters.view === 'list' && (
                        <DeckTable
                          decks={flatDecks}
                          categories={state.categories}
                          covers={covers}
                          warm={warm}
                          busy={busy}
                          selected={selected}
                          openMenuKey={openMenuKey}
                          draggingSlug={draggingSlug}
                          sort={filters.sort}
                          onSort={setSort}
                          onToggleSelected={toggleSelected}
                          setMenuKey={setOpenMenuKey}
                          setDraggingSlug={setDraggingSlug}
                          actions={cardActions}
                        />
                      )}
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        )}
      </main>

      {confirm && <ConfirmModal {...confirm} busy={busy} onCancel={() => setConfirm(null)} />}
      {nameModal && (
        <DeckNameModal
          key={`${nameModal.mode}:${nameModal.deck.slug}`}
          mode={nameModal.mode}
          deck={nameModal.deck}
          decks={state.decks}
          slugPrefix={state.slugPrefix}
          initialName={nameModal.initialName}
          busy={busy}
          onCancel={() => setNameModal(null)}
          onConfirm={nameModal.mode === 'rename' ? onRenameConfirm : onDuplicateConfirm}
        />
      )}
      {infoDeck && <DeckInfoModal deck={infoDeck} onClose={() => setInfoDeck(null)} />}
      {moveDeck && (
        <MoveToCategoryModal
          deck={moveDeck}
          allCategories={allCategories}
          busy={busy}
          onCancel={() => setMoveDeck(null)}
          // Single mode: a path moves, null clears. The array arm belongs to
          // the modal's retired multi mode and cannot arrive here.
          onSave={(next) => onSetDeckCategory(moveDeck, typeof next === 'string' ? next : null)}
        />
      )}
      {visibilityDeck && (
        <DeckVisibilityModal
          deck={visibilityDeck}
          people={state.people}
          me={state.people.find((p) => p.id === meId) ?? null}
          busy={busy}
          onCancel={() => setVisibilityDeck(null)}
          onSave={(next) => onSetDeckVisibility(visibilityDeck, next)}
        />
      )}
      {bulkCats && (
        <BulkCategoryModal
          decks={flatDecks.filter((d) => selected.has(d.slug))}
          allCategories={allCategories}
          busy={busy}
          onCancel={() => setBulkCats(false)}
          onSave={onBulkCategories}
        />
      )}
      {saveDeck && (
        <SaveToLibraryModal
          slug={saveDeck.slug}
          deckTitle={saveDeck.title || saveDeck.slug}
          busy={busy}
          onCancel={() => setSaveDeck(null)}
          onSave={onSaveToLibrary}
        />
      )}
      {manageCats && (
        <ManageCategoriesModal
          tree={categoryTree}
          busy={busy}
          onClose={() => setManageCats(false)}
          onCreate={onCreateCategory}
          onRename={onRenameCategoryPath}
          onDelete={onDeleteCategoryPath}
          onReorder={onReorderCategories}
          onSetColour={onSetCategoryColour}
          onCopyFilterLink={onCopyFilterLink}
          onCopyPublicLink={onCopyPublicLink}
        />
      )}
      {/* Read-only by construction: DeckOverview derives `editable` from
          onReorder + onDeleteSlides, so omitting them (and the section
          handlers) leaves looking, searching, sizing and opening — no drag, no
          delete, no rename. */}
      {overview?.status === 'ready' && (
        <DeckOverview
          slug={overview.slug}
          slides={overview.slides}
          sections={overview.sections}
          onSelect={(idx) => openDeckAtSlide(overview.slug, idx)}
          onClose={() => setOverview(null)}
        />
      )}
      {overview?.status === 'error' && (
        <ConfirmModal
          title="Could not read that deck's slides"
          body={overview.error}
          confirmLabel="Open in editor"
          onCancel={() => setOverview(null)}
          onConfirm={() => navigate(`/editor/${overview.slug}`)}
        />
      )}
    </div>
  );
}
